import { useState, useEffect, lazy, useRef, useCallback } from "react";
import { InnerBlocks } from "@wordpress/block-editor";
import {
  BlockWrapper,
  ChildrenBlock,
  defineBlock,
  smartProps,
  useEditor,
  useInspectorControl,
} from "@smartfire/js-block-engine";
import style from "./block.module.scss";
import { ReactComponent as PlayerButton } from "../assets/icons/play_circle.svg";
import { ReactComponent as DownIcon } from "../assets/icons/down.svg";
import { EditableImg } from "@smartfire/components";
import useMediaQuery from "@mui/material/useMediaQuery";
import clsx from "clsx";

const InspectorLazy = lazy(() => import("./Inspector"));

function Block({ videoId, fullHeight, contentPosition, image }) {
  useInspectorControl(InspectorLazy, []);

  const { isEditing } = useEditor();
  const isMobile = useMediaQuery("(max-width: " + style.mobileBreakpoint + ")");

  const playerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const initializePlayer = useCallback(() => {
    if (playerRef.current && window.YT && window.YT.Player) {
      new window.YT.Player(playerRef.current, {
        videoId: videoId,
        height: "100%",
        width: "100%",
        playerVars: {
          autoplay: 1,
          enablejsapi: 1,
          playlist: videoId,
          disablekb: 1,
          loop: 1,
          showinfo: 0,
          controls: 0,
          mute: 1,
          rel: 0,
          modestbranding: 1,
          fs: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: (event) => {
            setPlayer(event.target);
          },
          onError: (error) => {
            console.error("YouTube Player Error:", error);
          },
        },
      });
    }
  }, [videoId]);

  useEffect(() => {
    let script = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    );
    if (!script) {
      script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.body.appendChild(script);
    }

    window.youtubeIframeAPIReadyCallbacks =
      window.youtubeIframeAPIReadyCallbacks || [];
    window.youtubeIframeAPIReadyCallbacks.push(() => {
      initializePlayer();
    });

    // Assurez-vous que la fonction onYouTubeIframeAPIReady existe
    if (!window.onYouTubeIframeAPIReady) {
      window.onYouTubeIframeAPIReady = () => {
        window.youtubeIframeAPIReadyCallbacks.forEach((callback) => {
          try {
            callback();
          } catch (error) {
            console.error("Error in YouTube API Ready Callback:", error);
          }
        });
      };
    }

    return () => {
      script.remove();
      if (player) {
        player.destroy();
      }
    };
  }, [videoId, player, initializePlayer]);

  const handlePlay = () => {
    if (player) {
      player.playVideo();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (player) {
      player.pauseVideo();
      setIsPlaying(false);
    }
  };

  const handleToggle = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  return (
    <BlockWrapper.Div
      className={clsx(
        style.block,
        !fullHeight && style.notFullHeight,
        style[contentPosition]
      )}
      style={{
        // background: image ? `#00000066, url(${image.url})` : "",
        background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00)0%, rgba(0, 0, 0, 0.40)0%), center/cover no-repeat url(${
          image && image.url
        })`,
      }}
    >
      {fullHeight && (
        <div className={style.video}>
          {!videoId ? (
            <EditableImg smartProp="image" />
          ) : (
            <>
              <div ref={playerRef} id="primary_player"></div>
              <div className={style.videoPlayer} onClick={handleToggle}>
                {!isPlaying && <PlayerButton fill="#00000033" />}
              </div>
            </>
          )}
        </div>
      )}
      <div className={style.texts} onClick={handleToggle}>
        {isEditing ? (
          <InnerBlocks
            allowedBlocks={[
              "core/paragraph",
              "core/heading",
              "smartfire/button",
            ]}
            template={[
              [
                "core/heading",
                {
                  content: "Un domaine de montagne unique au monde",
                  placeholder: "My placeholder",
                },
              ],
              [
                "core/paragraph",
                {
                  content:
                    "Aeon est une vision commune, un fil rouge, un label de qualité qui se traduit dans chacune des expériences offertes dans nos 3 domaines : l’Alpe d’Huez, Les 2 Alpes et la Grave.",
                  placeholder: "Renseigner du texte...",
                },
              ],
              [
                "smartfire/button",
                {
                  text: "Découvrez l’univers aeon",
                  mode: "outlined",
                },
              ],
            ]}
          />
        ) : (
          <ChildrenBlock />
        )}
      </div>
      {!isMobile && fullHeight && (
        <div className={style.icon}>
          <DownIcon />
        </div>
      )}
    </BlockWrapper.Div>
  );
}

export default defineBlock({
  Block,
  smartProps: {
    videoId: smartProps.string.withDefault(""),
    image: smartProps.image,
    fullHeight: smartProps.bool.withDefault(false),
    contentPosition: smartProps.string.withDefault("center"),
  },
  setup: {
    title: "New Hero Home",
  },
});
